//DBTables
import { ModuleTypeId } from "../../../types/DBTables";
import { UseFormReturn } from "react-hook-form";

import NumberEditor from "../../Global/Editors/NumberEditor";
import { useGlobalData } from "../../GlobalDataProvider";
import SelectEditor from "../../Global/Editors/SelectEditor";
import { IModuleRound, useRound } from "../../../modules/roundHandler";
import { nameof } from "../../Global/Utils";
import { IModuleVentilationFXochF, IModuleVentilationFXochFRound } from "../../../types/ModuleDatas";
import TidsKanalEditor from "./TidsKanalEditor";
import { WeekDayName } from "../../../types/module-type";
import notify from "devextreme/ui/notify";
import RoundVarmePrimarPump from "./RoundVarmePrimarPump";
import { MapTooltip } from "../../../types/globalData";
import BFLNote from "../BFL/BFLNote";

interface IProps {
  module: IModuleRound;
  data: any;
  formHook: UseFormReturn;
}

const RoundVentilationFXochF = ({ module, data, formHook }: IProps) => {
  const globalData = useGlobalData();
  const roundHandler = useRound();

  //const moduleData = data as IModuleVentilationFXochF;

  const isFX = module.moduleTypeId === ModuleTypeId.VentilationFX;

  const setChannelFromFromLastRound = (channelName: string) => {
    const lastRound = roundHandler.getLastFinishedRound();
    if (!lastRound) {
      notify("Hittar ingen tidigare klarmarkerad rondering", "error");
      return;
    }
    const thisModuleLastRound = lastRound.data.modules.find((m) => m.id === module.id);
    if (thisModuleLastRound) {
      const roundTidsKanaler = thisModuleLastRound.data.tidsKanaler;
      const bfsTidsKanaler = thisModuleLastRound.bfs?.BFL_J3_FTX_FT?.data?.tidsKanaler;

      const kanal = (bfsTidsKanaler ? bfsTidsKanaler[channelName] : undefined) || (roundTidsKanaler ? roundTidsKanaler[channelName] : undefined);

      if (kanal) formHook.setValue("tidsKanaler." + channelName, kanal);
    }
  };

  const setDaysFrom = (channelName: string, dayName: WeekDayName, appliesTo: WeekDayName[]) => {
    var data = formHook.getValues();
    var tidsKanaler = data.tidsKanaler;
    if (tidsKanaler) {
      const kanal = tidsKanaler[channelName];
      if (kanal) {
        const periods = kanal[dayName];
        if (periods) {
          appliesTo.forEach((wd) => {
            kanal[wd] = periods.slice();
          });
          formHook.setValue("tidsKanaler." + channelName, kanal);
        }
      }
    }
  };

  const tidsKanaler = [
    {
      channelPath: "tidsKanaler",
      fieldName: "ventilation",
      caption: "Ventilation",
      hasType: true,
      buttons: [
        { caption: "Hämta schema från föregående rondering", callback: (dayName: WeekDayName) => setChannelFromFromLastRound("ventilation") },
        {
          caption: "Applicera detta dagsschema för måndag-fredag",
          callback: (dayName: WeekDayName) =>
            setDaysFrom("ventilation", dayName, [WeekDayName.monday, WeekDayName.tuesday, WeekDayName.wednesday, WeekDayName.thursday, WeekDayName.friday]),
        },
        {
          caption: "Applicera detta dagsschema för lördag-söndag",
          callback: (dayName: WeekDayName) => setDaysFrom("ventilation", dayName, [WeekDayName.saturday, WeekDayName.sunday]),
        },
        {
          caption: "Applicera detta dagsschema för måndag-söndag",
          callback: (dayName: WeekDayName) =>
            setDaysFrom("ventilation", dayName, [
              WeekDayName.monday,
              WeekDayName.tuesday,
              WeekDayName.wednesday,
              WeekDayName.thursday,
              WeekDayName.friday,
              WeekDayName.saturday,
              WeekDayName.sunday,
            ]),
        },
      ],
    },
    {
      channelPath: "tidsKanaler",
      fieldName: "närvaro",
      caption: "Närvaro",
      hasType: false,
      buttons: [
        { caption: "Hämta schema från föregående rondering", callback: (dayName: WeekDayName) => setChannelFromFromLastRound("närvaro") },
        {
          caption: "Applicera detta dagsschema för måndag-fredag",
          callback: (dayName: WeekDayName) =>
            setDaysFrom("närvaro", dayName, [WeekDayName.monday, WeekDayName.tuesday, WeekDayName.wednesday, WeekDayName.thursday, WeekDayName.friday]),
        },
        {
          caption: "Applicera detta dagsschema för lördag-söndag",
          callback: (dayName: WeekDayName) => setDaysFrom("närvaro", dayName, [WeekDayName.saturday, WeekDayName.sunday]),
        },
        {
          caption: "Applicera detta dagsschema för måndag-söndag",
          callback: (dayName: WeekDayName) =>
            setDaysFrom("närvaro", dayName, [
              WeekDayName.monday,
              WeekDayName.tuesday,
              WeekDayName.wednesday,
              WeekDayName.thursday,
              WeekDayName.friday,
              WeekDayName.saturday,
              WeekDayName.sunday,
            ]),
        },
      ],
    },
  ];

  const frmTidsKanaler = formHook.watch("tidsKanaler");
  const driftÅretRunt = isFX ? formHook.watch("driftÅretRunt") : "Nej";

  const ventilationStoppadPeriodvis = formHook.watch("ventilationStoppadPeriodvis");
  const luftflödesUppgiftList = globalData.data?.Ventilation.LuftflödesUppgift.map((v) => v.LuftflödesUppgift) || [];
  const luftflödesEnhetList = globalData.data?.Ventilation.LuftflödesEnhet.map((v) => v.LuftflödesEnhet) || [];

  const rektStorlekList = globalData.data?.Ventilation.KanalstorlekRektangulära.map((v) => v.KanalstorlekRektangulära) || [];
  const cirkStorlekList = globalData.data?.Ventilation.KanalstorlekCirkulära.map((v) => v.KanalstorlekCirkulära) || [];

  const flödeUppgift = formHook.watch("flödeUppgift");
  const flödesKanalForm = formHook.watch("flödesKanalForm");
  const flödeUppgiftAsSchablon = flödeUppgift === "Uppgift saknas";

  //moduleData
  const moduleData = module.data as IModuleVentilationFXochF;
  const reduceratFlödeFlödesreglering =
    moduleData.harReduceradeLuftflödeFlödesreglering === "Ja" && moduleData.reduceratFlödeFlödesreglering ? moduleData.reduceratFlödeFlödesreglering : 0;
  const reduceratFlödeTryckreglering =
    moduleData.harReduceradeLuftflödeTryckreglering === "Ja" && moduleData.reduceratFlödeTryckreglering ? moduleData.reduceratFlödeTryckreglering : 0;
  const fxJ3FlödeDelfart = Math.max(reduceratFlödeFlödesreglering, reduceratFlödeTryckreglering) / 100;
  const använderReduceratFlöde = fxJ3FlödeDelfart > 0;

  return (
    <div>
      <div className="row ml-0 align-items-end">
        <h5>Flöde ventilationsaggregat</h5>
      </div>
      <div className="col-lg-12">
        <SelectEditor
          label="Uppgift om luftflöden"
          fieldName={nameof<IModuleVentilationFXochFRound>("flödeUppgift")}
          tooltipId={nameof<MapTooltip>("VentilationFXoF_flödeUppgift")}
          items={luftflödesUppgiftList}
          formHook={formHook}
        />
      </div>
      {flödeUppgiftAsSchablon ? (
        <div className="row ml-0  align-items-end">
          <div className="col-lg-6">
            <SelectEditor
              label="Ange form på luftkanaler"
              fieldName={nameof<IModuleVentilationFXochFRound>("flödesKanalForm")}
              tooltipId={nameof<MapTooltip>("VentilationFXoF_flödesKanalForm")}
              items={["", "Cirkulära", "Rektangulära"]}
              formHook={formHook}
              options={{
                validate: {
                  required: (v) => {
                    return !v ? "Måste fyllas i" : undefined;
                  },
                },
              }}
            />
          </div>
          {flödesKanalForm === "Cirkulära" ? (
            <div className="col-lg-6">
              <SelectEditor
                label="Ange kanalstorlek (cirkulära)"
                fieldName={nameof<IModuleVentilationFXochFRound>("flödeFrånluftDiameter")}
                tooltipId={nameof<MapTooltip>("VentilationFXoF_flödeFrånluftDiameter")}
                items={cirkStorlekList}
                formHook={formHook}
                options={{ required: "Måste göra ett val" }}
              />
            </div>
          ) : (
            <div className="col-lg-6">
              <SelectEditor
                label="Ange kanalstorlek (rektangulära)"
                fieldName={nameof<IModuleVentilationFXochFRound>("flödeKanalstorlekBxH")}
                tooltipId={nameof<MapTooltip>("VentilationFXoF_flödeKanalstorlekBxH")}
                items={rektStorlekList}
                formHook={formHook}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="row ml-0  align-items-end">
          <div className="col-lg-6">
            <NumberEditor
              label="Flöde frånluft"
              fieldName={nameof<IModuleVentilationFXochFRound>("flödeFrånluft")}
              tooltipId={nameof<MapTooltip>("VentilationFXoF_flödeFrånluft")}
              formHook={formHook}
              min={0}
              options={{
                validate: {
                  required: (v) => {
                    return !v ? "Måste fyllas i" : undefined;
                  },
                },
              }}
            />
          </div>
          <div className="col-lg-6">
            <SelectEditor
              label="Enhet för luftflöden"
              fieldName={nameof<IModuleVentilationFXochFRound>("flödeEnhet")}
              tooltipId={nameof<MapTooltip>("VentilationFXoF_flödeEnhet")}
              items={luftflödesEnhetList}
              formHook={formHook}
            />
          </div>
        </div>
      )}
      {isFX ? (
        <div className="row ml-0 align-items-end">
          <div className="col-lg-6">
            <SelectEditor
              label="Har frånluftsfläkten dygnet runt drift hela året?"
              fieldName={nameof<IModuleVentilationFXochF>("driftÅretRunt")}
              tooltipId={nameof<MapTooltip>("VentilationFXoF_driftÅretRunt")}
              formHook={formHook}
              items={["", "Ja", "Nej"]}
              options={{
                validate: {
                  req: (value) => (value ? undefined : "Ange Ja/Nej"),
                },
              }}
            />
          </div>
          {/* Om fxJ3FlödeDelfart är 0 så ska inte delfartfaktor visas och automatiskt bli 0 */}
          {använderReduceratFlöde ? (
            <div className="col-lg-6">
              <NumberEditor
                label="Delfartsfaktor (%)"
                fieldName={nameof<IModuleVentilationFXochF>("delfartsFaktor")}
                tooltipId={nameof<MapTooltip>("VentilationFXoF_delfartsFaktor")}
                formHook={formHook}
                isDisabled={driftÅretRunt !== "Ja"}
                options={{
                  required: "Ange andel i procent",
                  validate: {
                    min: (value) => (value >= 0 ? undefined : "Måste vara minst 0"),
                    max: (value) => (value <= 100 ? undefined : "Måste vara högst 100"),
                  },
                }}
              />
            </div>
          ) : (
            <input type="hidden" value={0} {...formHook.register(nameof<IModuleVentilationFXochF>("delfartsFaktor"))} />
          )}
        </div>
      ) : (
        <div className="col-lg-4">
          <input type="hidden" value={"Nej"} defaultValue={"Nej"} {...formHook.register(nameof<IModuleVentilationFXochF>("driftÅretRunt"))} className={"form-control "} />
        </div>
      )}

      {/********************  Tidschema ventilationsaggregat & verksamhet  */}
      {driftÅretRunt === "Nej" ? (
        <>
          <div className="row ml-0 mt-4">
            <h5>Tidschema ventilationsaggregat & verksamhet </h5>
          </div>
          {!isFX ? (
            <>
              <div className="row ml-0 align-items-end">
                <div className="col-lg-4">
                  <SelectEditor
                    label="Finns det orsaker till lång drifttid för ventilationsaggregat?"
                    fieldName={nameof<IModuleVentilationFXochF>("finnsOrsakerTillLångDrifttid")}
                    tooltipId={nameof<MapTooltip>("VentilationFXoF_finnsOrsakerTillLångDrifttid")}
                    formHook={formHook}
                    items={["", "Ja", "Nej"]}
                    options={{
                      validate: {
                        req: (value) => (value ? undefined : "Ange Ja/Nej"),
                      },
                    }}
                  />
                </div>
                <div className="col-lg-4">
                  <SelectEditor
                    label="Är nattkyla aktiverad?"
                    fieldName={nameof<IModuleVentilationFXochF>("nattkylaAktiverad")}
                    tooltipId={nameof<MapTooltip>("VentilationFXoF_nattkylaAktiverad")}
                    formHook={formHook}
                    items={["", "Ja", "Nej"]}
                    options={{
                      validate: {
                        req: (value) => (value ? undefined : "Ange Ja/Nej"),
                      },
                    }}
                  />
                </div>
                <div className="col-lg-4">
                  <SelectEditor
                    label="Är ventilationsanläggningen stoppad periodvis under året?"
                    fieldName={nameof<IModuleVentilationFXochF>("ventilationStoppadPeriodvis")}
                    tooltipId={nameof<MapTooltip>("VentilationFXoF_ventilationStoppadPeriodvis")}
                    formHook={formHook}
                    items={["Ja", "Nej"]}
                    options={{
                      validate: {
                        req: (value) => (value ? undefined : "Ange Ja/Nej"),
                      },
                    }}
                  />
                </div>
              </div>
              <div className="row ml-0 align-items-end">
                <div className="col-lg-6">
                  <NumberEditor
                    isDisabled={ventilationStoppadPeriodvis !== "Ja"}
                    label="Ange antal veckor som ventilationsanläggningen är stoppad per år"
                    fieldName={nameof<IModuleVentilationFXochF>("antalVeckorVentilationStoppadPerÅr")}
                    tooltipId={nameof<MapTooltip>("VentilationFXoF_antalVeckorVentilationStoppadPerÅr")}
                    formHook={formHook}
                    min={0}
                    max={52}
                    options={{ required: "Måste fyllas i" }}
                  />
                </div>
              </div>
            </>
          ) : (
            <></>
          )}
          <div className="col-lg-12">
            <TidsKanalEditor data={data} formHook={formHook} tidsKanaler={tidsKanaler}></TidsKanalEditor>
          </div>
        </>
      ) : (
        <></>
      )}
      {isFX ? (
        <>
          <div className="row ml-0 mt-4">
            <h5>Värmepump </h5>
          </div>
          <RoundVarmePrimarPump module={module} data={data} formHook={formHook} usedAsFXSubsystem={true} />
        </>
      ) : (
        <></>
      )}
      <BFLNote formHook={formHook} />
    </div>
  );
};

export default RoundVentilationFXochF;
